import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, Bell, X, MessageCircle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import NotificationsDropdown from './NotificationsDropdown';

import ContactWidget from './ContactWidget';

const FloatingActions = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [helpVisible, setHelpVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    const handleSidebarState = (e) => {
      setIsSidebarOpen(e.detail.open);
    };
    window.addEventListener('sidebar-state', handleSidebarState);
    return () => window.removeEventListener('sidebar-state', handleSidebarState);
  }, []);

  // Open the support widget automatically when an account is disabled, so the
  // user can immediately reach out to the admin.
  useEffect(() => {
    const handleAccountDisabled = () => setIsContactOpen(true);
    window.addEventListener('account-disabled', handleAccountDisabled);
    return () => window.removeEventListener('account-disabled', handleAccountDisabled);
  }, []);

  useEffect(() => {
    fetch('/api/settings/support-widget')
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data) {
          setHelpVisible(data.whatsappEnabled || data.messageFormEnabled);
        } else {
          setHelpVisible(true); // fallback: show if fetch fails
        }
      })
      .catch(() => setHelpVisible(true));
  }, []);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        const u = storedUser ? JSON.parse(storedUser) : null;
        const userId = u ? (u.u_id || u.user_id || u.id) : '';
        const res = await fetch(`/api/notifications/public${userId ? `?userId=${userId}` : ''}`);
        const data = await res.json();
        if (Array.isArray(data)) {
          setUnreadCount(data.filter(n => !n.read_by?.includes(userId)).length);
        }
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 10000);
    return () => clearInterval(interval);
  }, []);

  const primaryColor = "#f97316"; // Theme Orange

  const isAuthPage = ['/auth', '/login', '/signup'].includes(location.pathname);
  if (isAuthPage || isSidebarOpen) return null;

  return (
    <>
      <style>{`
        .sidebar-open .help-button-container {
          display: none !important;
        }
      `}</style>
      <div className="fixed bottom-24 sm:bottom-20 right-6 z-[100] flex flex-col items-end gap-4 help-button-container">
      {/* Notifications Dropdown */}
      <div className="relative">
        <NotificationsDropdown
          isOpen={isNotificationsOpen}
          onClose={() => setIsNotificationsOpen(false)}
          primaryColor={primaryColor}
        />
      </div>

      {/* Contact Widget */}
      {helpVisible && (
        <AnimatePresence>
          {isContactOpen && (
            <ContactWidget
              isOpen={isContactOpen}
              onClose={() => setIsContactOpen(false)}
              primaryColor={primaryColor}
            />
          )}
        </AnimatePresence>
      )}

      {helpVisible && (
        <div className="flex flex-col items-end gap-3">
          <div className="relative group">
            <button
              onClick={() => setIsContactOpen(!isContactOpen)}
              className="flex items-center gap-3 bg-[#0f172a] pl-5 pr-7 py-3.5 rounded-full shadow-2xl shadow-slate-900/20 hover:scale-105 active:scale-95 transition-all"
              style={{ color: primaryColor }}
            >
              {isContactOpen ? <X size={20} /> : <HelpCircle size={20} />}
              <span className="text-sm font-black tracking-tight uppercase">Help</span>
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default FloatingActions;
